import type { ToolResult } from "idfweb-tools";

/**
 * The messages between a page and the relay, as JSON over a WebSocket.
 *
 * A page registers the SHA-256 of its MCP token, in hex, with what an agent
 * can tell the tab by. The relay then sends it the tool calls that arrive at
 * that token's MCP URL and choose this tab, and the page answers each with
 * progress messages and one result.
 */

export type TabToRelay =
    | { type: "register"; tokenHash: string; tab?: TabInfo }
    /** The tab's project changed, or the user focused it. */
    | { type: "tab"; tab: TabInfo }
    | { type: "progress"; id: number; message: string }
    | { type: "result"; id: number; result: ToolResult };

export type RelayToTab =
    | { type: "registered" }
    | { type: "refused"; reason: string }
    /**
     * `agent` is the MCP client that last used this tab, as far as the relay
     * can tell clients apart: by the name the last `initialize` on the token
     * gave. `chosen` says whether calls that choose no tab come here.
     */
    | { type: "agent"; agent: AgentInfo | undefined; chosen: boolean }
    | { type: "call"; id: number; name: string; args: Record<string, unknown> };

/** What the relay knows of a tab, as the tab reports it. */
export interface TabInfo {
    /** The kept project open in the tab; none while the welcome window shows. */
    project?: TabProject;
    /** When the user last focused the tab, in milliseconds since the epoch; 0 if never. */
    focused: number;
}

export interface TabProject {
    /** The id in the tab's URL, `<site>/p/<id>`. */
    id: string;
    name: string;
    /** The name of the local folder the project is kept in; browsers give no path. */
    folder?: string;
}

/** Client information received in the initialize request. */
export interface AgentInfo {
    name: string;
    version: string;
}

/** The path of the WebSocket, and the path of the MCP endpoint without the token. */
export const RELAY_PATH = "relay";
export const MCP_PATH = "mcp";
